import React, { useState, useEffect } from 'react';
import { Shield, Smartphone, Mail, Phone, Fingerprint, Loader2, LogOut, CheckCircle } from 'lucide-react';
import { fetchMFAPreference, updateMFAPreference, updateUserAttributes, confirmUserAttribute, sendUserAttributeVerificationCode, fetchUserAttributes, setUpTOTP, verifyTOTPSetup, associateWebAuthnCredential, listWebAuthnCredentials } from 'aws-amplify/auth';
import { signOut } from '@aws-amplify/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserSettingsContext } from '../context/UserSettingsContext';
import { toast } from 'react-hot-toast';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { QRCodeSVG } from 'qrcode.react';
import { setPasskeyMfaEnabled } from '../utils/passkeyMfaAuth';

const METHOD_INFO = {
  TOTP: { icon: Smartphone, label: 'Authenticator App', desc: 'Google Authenticator, Authy, etc.' },
  EMAIL: { icon: Mail, label: 'Email Code', desc: 'Receive a code at your email' },
  SMS: { icon: Phone, label: 'Text Message (SMS)', desc: 'Receive a code via SMS' },
  PASSKEY: { icon: Fingerprint, label: 'Passkey / Security Key', desc: 'Device passkey or hardware key' },
};

const MfaEnforcementGuard = () => {
  const { currentOrganization } = useUserSettingsContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [compliant, setCompliant] = useState(true);
  const [allowedMethods, setAllowedMethods] = useState([]);
  const [saving, setSaving] = useState(false);

  // Setup flows
  const [activeSetup, setActiveSetup] = useState(null); // 'TOTP' | 'SMS' | 'EMAIL' | 'PASSKEY'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [totpUri, setTotpUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupError, setSetupError] = useState('');

  useEffect(() => {
    checkCompliance();
  }, [currentOrganization, location.pathname]);

  async function checkCompliance() {
    console.log('[MFA Guard] checkCompliance called');
    console.log('[MFA Guard] currentOrganization:', currentOrganization?.code, currentOrganization?.name);
    console.log('[MFA Guard] mfaPolicy:', JSON.stringify(currentOrganization?.mfaPolicy));

    const policy = currentOrganization?.mfaPolicy;
    if (!policy?.enforced) {
      console.log('[MFA Guard] Policy not enforced, skipping');
      setCompliant(true);
      setChecking(false);
      return;
    }

    const allowed = policy.allowedMethods || [];
    if (allowed.length === 0) {
      console.log('[MFA Guard] No allowed methods specified, skipping');
      setCompliant(true);
      setChecking(false);
      return;
    }

    console.log('[MFA Guard] MFA enforced, allowed methods:', allowed);
    setAllowedMethods(allowed);

    try {
      const [pref, passkeys] = await Promise.all([
        fetchMFAPreference(),
        allowed.includes('PASSKEY') ? listWebAuthnCredentials().catch(() => ({ credentials: [] })) : Promise.resolve({ credentials: [] })
      ]);

      console.log('[MFA Guard] User MFA preference:', JSON.stringify(pref));
      console.log('[MFA Guard] User passkeys:', passkeys.credentials?.length || 0);

      const userMethods = [];
      if (pref?.enabled?.includes('SMS') || pref?.preferred === 'SMS') userMethods.push('SMS');
      if (pref?.enabled?.includes('TOTP') || pref?.preferred === 'TOTP') userMethods.push('TOTP');
      if (pref?.enabled?.includes('EMAIL') || pref?.preferred === 'EMAIL') userMethods.push('EMAIL');
      if (passkeys.credentials?.length > 0) userMethods.push('PASSKEY');

      const isCompliant = userMethods.some(m => allowed.includes(m));
      console.log('[MFA Guard] User methods:', userMethods, '| Compliant:', isCompliant);
      setCompliant(isCompliant);
    } catch (err) {
      console.error('[MFA Guard] Compliance check error:', err);
      setCompliant(true); // Don't block on error
    } finally {
      setChecking(false);
    }
  }

  // --- Setup handlers ---
  async function handleEnableEmail() {
    setSaving(true); setSetupError('');
    try {
      await updateMFAPreference({ email: 'ENABLED' });
      toast.success('Email MFA enabled');
      await checkCompliance();
    } catch { setSetupError('Error enabling email MFA.'); }
    finally { setSaving(false); }
  }

  async function handleSmsSendCode(e) {
    e.preventDefault(); setSaving(true); setSetupError('');
    if (!phoneNumber) { setSetupError('Enter a phone number.'); setSaving(false); return; }
    try {
      await updateUserAttributes({ userAttributes: { phone_number: phoneNumber } });
      await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      setSmsCodeSent(true); toast('Code sent.');
    } catch (err) { console.error('Error sending SMS verification code:', err); setSetupError('Error sending code.'); }
    finally { setSaving(false); }
  }

  async function handleSmsVerify(e) {
    e.preventDefault(); setSaving(true); setSetupError('');
    try {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: smsCode });
      await updateMFAPreference({ sms: 'ENABLED' });
      toast.success('SMS MFA enabled');
      setActiveSetup(null);
      await checkCompliance();
    } catch { setSetupError('Invalid code.'); setSmsCode(''); }
    finally { setSaving(false); }
  }

  async function handleTotpStart() {
    setSaving(true); setSetupError('');
    try {
      const attrs = await fetchUserAttributes();
      const setup = await setUpTOTP();
      setTotpUri(setup.getSetupUri('Lodgeic', attrs.email || '').toString());
      setActiveSetup('TOTP');
    } catch { setSetupError('Error starting authenticator setup.'); }
    finally { setSaving(false); }
  }

  async function handleTotpVerify(e) {
    e.preventDefault(); setSaving(true); setSetupError('');
    try {
      await verifyTOTPSetup({ code: totpCode });
      await updateMFAPreference({ totp: 'ENABLED' });
      toast.success('Authenticator MFA enabled');
      setActiveSetup(null);
      await checkCompliance();
    } catch { setSetupError('Invalid code.'); setTotpCode(''); }
    finally { setSaving(false); }
  }

  async function handlePasskey() {
    setSaving(true); setSetupError('');
    try {
      await associateWebAuthnCredential();
      // Also enable the passkey as a WebAuthn MFA factor so it can be used at login.
      // May fail if the user has no backup MFA method — the passkey itself still counts
      // toward compliance, so don't block on it.
      try {
        await setPasskeyMfaEnabled(true);
      } catch (mfaErr) {
        console.error('Failed to enable passkey MFA preference:', mfaErr);
      }
      toast.success('Passkey registered');
      await checkCompliance();
    } catch (err) {
      if (err.name === 'NotAllowedError') setSetupError('Passkey registration cancelled.');
      else setSetupError('Error registering passkey.');
    } finally { setSaving(false); }
  }

  async function handleSignOut() {
    try { await signOut(); navigate('/login'); } catch {}
  }

  // Don't render anything if compliant or still checking
  if (checking || compliant) return null;

  const orgName = currentOrganization?.name || 'Your organization';
  const inputStyle = { border: '1px solid var(--lodge-onyx-100)', background: 'var(--lodge-white)', color: 'var(--lodge-onyx-900)' };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg mx-4 rounded-2xl overflow-hidden" style={{ background: 'var(--lodge-white)', boxShadow: 'var(--shadow-xl)' }}>
        {/* Header */}
        <div className="p-6 text-center" style={{ borderBottom: '1px solid var(--lodge-onyx-100)' }}>
          <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--lodge-orange-50)' }}>
            <Shield size={28} style={{ color: 'var(--lodge-orange-400)' }} />
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--lodge-onyx-900)' }}>Two-Factor Authentication Required</h2>
          <p style={{ fontSize: 13, color: 'var(--lodge-onyx-500)', marginTop: 6 }}>
            {orgName} requires two-factor authentication. Set up an approved method to continue.
          </p>
        </div>

        {/* Methods */}
        <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
          {allowedMethods.map(method => {
            const info = METHOD_INFO[method];
            if (!info) return null;
            const Icon = info.icon;
            const isActive = activeSetup === method;

            return (
              <div key={method} className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--lodge-onyx-100)' }}>
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg" style={{ background: 'var(--lodge-onyx-50)' }}>
                      <Icon size={16} style={{ color: 'var(--lodge-onyx-500)' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--lodge-onyx-900)' }}>{info.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>{info.desc}</div>
                    </div>
                  </div>
                  {method === 'EMAIL' && !isActive && (
                    <button onClick={handleEnableEmail} disabled={saving}
                      className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ background: 'var(--lodge-orange-400)' }}>
                      {saving ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                  {method === 'TOTP' && !isActive && (
                    <button onClick={handleTotpStart} disabled={saving}
                      className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ background: 'var(--lodge-orange-400)' }}>
                      Set Up
                    </button>
                  )}
                  {method === 'SMS' && !isActive && (
                    <button onClick={() => { setActiveSetup('SMS'); setSetupError(''); }} disabled={saving}
                      className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ background: 'var(--lodge-orange-400)' }}>
                      Set Up
                    </button>
                  )}
                  {method === 'PASSKEY' && !isActive && (
                    <button onClick={handlePasskey} disabled={saving}
                      className="px-3 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                      style={{ background: 'var(--lodge-orange-400)' }}>
                      {saving ? 'Registering...' : 'Register'}
                    </button>
                  )}
                </div>

                {/* Inline TOTP setup */}
                {method === 'TOTP' && isActive && (
                  <form onSubmit={handleTotpVerify} className="px-4 pb-4 space-y-3">
                    <p style={{ fontSize: 12, color: 'var(--lodge-onyx-500)' }}>Scan with your authenticator app, then enter the code.</p>
                    <div className="flex justify-center">
                      <div className="p-3 rounded-xl" style={{ background: '#fff', boxShadow: 'var(--shadow-md)' }}>
                        <QRCodeSVG value={totpUri} size={140} />
                      </div>
                    </div>
                    <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))} autoFocus
                      className="w-full px-3 py-2.5 rounded-lg text-center text-xl tracking-[0.4em] font-mono" style={inputStyle} />
                    {setupError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{setupError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setActiveSetup(null); setTotpCode(''); setSetupError(''); }}
                        className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Cancel</button>
                      <button type="submit" disabled={saving || totpCode.length < 6}
                        className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                        style={{ background: 'var(--lodge-orange-400)' }}>{saving ? 'Verifying...' : 'Verify'}</button>
                    </div>
                  </form>
                )}

                {/* Inline SMS setup */}
                {method === 'SMS' && isActive && !smsCodeSent && (
                  <form onSubmit={handleSmsSendCode} className="px-4 pb-4 space-y-3">
                    <PhoneInput international defaultCountry="US" value={phoneNumber} onChange={setPhoneNumber}
                      className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--lodge-onyx-100)' }} />
                    {setupError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{setupError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setActiveSetup(null); setPhoneNumber(''); setSetupError(''); }}
                        className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Cancel</button>
                      <button type="submit" disabled={saving || !phoneNumber}
                        className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                        style={{ background: 'var(--lodge-orange-400)' }}>{saving ? 'Sending...' : 'Send Code'}</button>
                    </div>
                  </form>
                )}
                {method === 'SMS' && isActive && smsCodeSent && (
                  <form onSubmit={handleSmsVerify} className="px-4 pb-4 space-y-3">
                    <input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={smsCode}
                      onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, ''))} autoFocus
                      className="w-full px-3 py-2.5 rounded-lg text-center text-xl tracking-[0.4em] font-mono" style={inputStyle} />
                    {setupError && <p style={{ fontSize: 12, color: 'var(--color-error)' }}>{setupError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={() => { setSmsCodeSent(false); setSmsCode(''); setSetupError(''); }}
                        className="px-3 py-1.5 text-sm rounded-lg" style={{ background: 'var(--lodge-onyx-50)', color: 'var(--lodge-onyx-600)' }}>Back</button>
                      <button type="submit" disabled={saving || smsCode.length < 6}
                        className="px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50"
                        style={{ background: 'var(--lodge-orange-400)' }}>{saving ? 'Verifying...' : 'Verify'}</button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}

          {setupError && !activeSetup && (
            <p style={{ fontSize: 12, color: 'var(--color-error)', textAlign: 'center' }}>{setupError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 text-center" style={{ borderTop: '1px solid var(--lodge-onyx-100)' }}>
          <button onClick={handleSignOut} className="flex items-center gap-2 mx-auto text-sm transition-colors"
            style={{ color: 'var(--lodge-onyx-400)' }}>
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default MfaEnforcementGuard;
